import React, { useState, useEffect } from 'react';
import { Card, Space, Typography, Spin, message, Button, Row, Col } from 'antd';
import { PlusOutlined, ReloadOutlined, FileTextOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { ViewToggle, ViewMode } from './ViewToggle';
import { DateNavigator } from './DateNavigator';
import { EmployeeFilter } from './EmployeeFilter';
import { TimelineView } from './TimelineView';
import { EmployeeCardGrid } from './EmployeeCardGrid';
import { QuickBookingDrawer } from './QuickBookingDrawer';
import { CancelledAppointmentsView } from './CancelledAppointmentsView';
import {
  AppointmentDetailModal,
  RescheduleModal,
  TechnicianChangeModal,
  CancelAppointmentModal
} from '../Appointments';
import { appointmentApi, Appointment } from '../../services/appointmentApi';
import { getTokens } from '../../services/authApi';
import './SchedulingHub.css';

const { Title, Text } = Typography;

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

interface TechnicianSchedule {
  id: number;
  user_id: number;
  day_of_week: number; // 0=Sunday, 1=Monday, etc.
  start_time: string; // "HH:MM"
  end_time: string; // "HH:MM"
  is_active: boolean;
}

interface ScheduleException {
  id: number;
  user_id: number;
  exception_type: 'time_off' | 'break' | 'special_hours' | 'holiday';
  start_datetime: string;
  end_datetime: string;
  reason?: string;
}

export const SchedulingHub: React.FC = () => {
  const navigate = useNavigate();
  const headerRef = React.useRef<HTMLDivElement>(null);
  const lastWidthRef = React.useRef<number>(window.innerWidth);

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [showCancelledView, setShowCancelledView] = useState(false);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);

  // Data state
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [schedules, setSchedules] = useState<Map<number, TechnicianSchedule[]>>(new Map());
  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);
  const [loading, setLoading] = useState(false);

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  // Modal states
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [rescheduleModalVisible, setRescheduleModalVisible] = useState(false);
  const [techChangeModalVisible, setTechChangeModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [modalAppointment, setModalAppointment] = useState<Appointment | null>(null);

  // Handle container resize for responsive design (accounts for sidebar)
  useEffect(() => {
    const updateContainerWidth = () => {
      if (headerRef.current) {
        const width = headerRef.current.offsetWidth;
        // Only update if width changed by more than 10px to prevent infinite loops
        if (Math.abs(width - lastWidthRef.current) > 10) {
          lastWidthRef.current = width;
          setContainerWidth(width);
        }
      }
    };

    // Initial measurement with small delay to avoid immediate loop
    const timer = setTimeout(updateContainerWidth, 100);

    // Update on window resize with debouncing
    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateContainerWidth, 150);
    };

    window.addEventListener('resize', handleResize);

    // Use ResizeObserver to detect container size changes (when sidebar opens/closes)
    let resizeObserver: ResizeObserver | null = null;
    try {
      resizeObserver = new ResizeObserver(() => {
        // Debounce the observer callback
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(updateContainerWidth, 150);
      });

      if (headerRef.current) {
        resizeObserver.observe(headerRef.current);
      }
    } catch (error) {
      console.warn('ResizeObserver not supported:', error);
    }

    return () => {
      clearTimeout(timer);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  // Load initial data
  useEffect(() => {
    loadEmployees();
    loadSchedules();
    loadAppointments();
  }, []);

  // Reload appointments and exceptions when date or employees change
  useEffect(() => {
    loadAppointments();
    loadExceptions();
  }, [selectedDate, viewMode]);

  const loadEmployees = async () => {
    try {
      // Load all technicians and owners using the auth API
      const tokens = getTokens();
      const headers: HeadersInit = tokens?.accessToken ? {
        'Authorization': `Bearer ${tokens.accessToken}`
      } : {};

      // Fetch both technicians and owners
      const [techniciansResponse, ownersResponse] = await Promise.all([
        fetch('/api/users?role=technician&status=active', { headers }),
        fetch('/api/users?role=owner&status=active', { headers })
      ]);

      const technicians = await techniciansResponse.json();
      const owners = await ownersResponse.json();

      // Ensure we have arrays
      const techArray = Array.isArray(technicians) ? technicians : [];
      const ownerArray = Array.isArray(owners) ? owners : [];

      // Combine and sort by name
      const allUsers = [...techArray, ...ownerArray].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setEmployees(allUsers);
    } catch (error) {
      console.error('Error loading employees:', error);
      message.error('Failed to load employees');
      setEmployees([]); // Ensure employees is always an array
    }
  };

  const loadSchedules = async () => {
    try {
      const tokens = getTokens();
      const headers: HeadersInit = tokens?.accessToken ? {
        'Authorization': `Bearer ${tokens.accessToken}`
      } : {};

      const response = await fetch('/api/admin/schedules/technicians', { headers });
      const data = await response.json();

      // Convert array response to Map
      const scheduleMap = new Map<number, TechnicianSchedule[]>();
      if (Array.isArray(data)) {
        data.forEach((item: { user_id: number; schedules: TechnicianSchedule[] }) => {
          scheduleMap.set(item.user_id, item.schedules);
        });
      }

      setSchedules(scheduleMap);
    } catch (error) {
      console.error('Error loading schedules:', error);
      message.error('Failed to load employee schedules');
      setSchedules(new Map());
    }
  };

  const loadExceptions = async () => {
    try {
      const tokens = getTokens();
      if (!tokens?.accessToken) {
        console.warn('No access token available for loading exceptions');
        setExceptions([]);
        return;
      }

      const headers: HeadersInit = {
        'Authorization': `Bearer ${tokens.accessToken}`
      };

      // Determine date range based on view mode
      let startDate: string;
      let endDate: string;

      switch (viewMode) {
        case 'cards':
        case 'day':
          startDate = selectedDate.startOf('day').toISOString();
          endDate = selectedDate.endOf('day').toISOString();
          break;
        case 'week':
          startDate = selectedDate.startOf('week').toISOString();
          endDate = selectedDate.endOf('week').toISOString();
          break;
        case 'month':
          startDate = selectedDate.startOf('month').toISOString();
          endDate = selectedDate.endOf('month').toISOString();
          break;
        default:
          startDate = selectedDate.startOf('day').toISOString();
          endDate = selectedDate.endOf('day').toISOString();
      }

      const response = await fetch(
        `/api/admin/schedules/exceptions?start_date=${startDate}&end_date=${endDate}`,
        { headers }
      );

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('Unauthorized to load exceptions, using empty array');
          setExceptions([]);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setExceptions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading exceptions:', error);
      // Don't show error message to user, just use empty exceptions
      setExceptions([]);
    }
  };

  const loadAppointments = async () => {
    setLoading(true);
    try {
      // Determine date range based on view mode
      let startDate: string;
      let endDate: string;

      switch (viewMode) {
        case 'cards':
        case 'day':
          // Load current day
          startDate = selectedDate.startOf('day').toISOString();
          endDate = selectedDate.endOf('day').toISOString();
          break;
        case 'week':
          // Load current week
          startDate = selectedDate.startOf('week').toISOString();
          endDate = selectedDate.endOf('week').toISOString();
          break;
        case 'month':
          // Load current month
          startDate = selectedDate.startOf('month').toISOString();
          endDate = selectedDate.endOf('month').toISOString();
          break;
        default:
          startDate = selectedDate.startOf('day').toISOString();
          endDate = selectedDate.endOf('day').toISOString();
      }

      const data = await appointmentApi.getAll({
        start_date: startDate,
        end_date: endDate
      });

      setAppointments(data);
    } catch (error) {
      console.error('Error loading appointments:', error);
      message.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadAppointments();
    message.success('Appointments refreshed');
  };

  const handleCreateAppointment = () => {
    setSelectedAppointment(null);
    setDrawerVisible(true);
  };

  const handleCreateEstimate = () => {
    // Navigate to estimate creation page
    navigate('/admin/estimates/new');
  };

  const handleScheduleApprovedEstimate = () => {
    // Open drawer to schedule an approved estimate
    setSelectedAppointment(null);
    setDrawerVisible(true);
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    // Open detail modal instead of drawer
    handleViewDetails(appointment);
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedAppointment(null);
    loadAppointments();
  };

  const handleDateChange = (date: Dayjs) => {
    setSelectedDate(date);
  };

  const handleEmployeeFilterChange = (employeeIds: number[]) => {
    setSelectedEmployees(employeeIds);
  };

  // Modal handlers
  const handleViewDetails = (appointment: Appointment) => {
    console.log('handleViewDetails called with appointment:', appointment?.id);
    console.log('Current modal states before:', {
      detailModalVisible,
      rescheduleModalVisible,
      techChangeModalVisible,
      cancelModalVisible,
      modalAppointment: modalAppointment?.id
    });
    setModalAppointment(appointment);
    setDetailModalVisible(true);
  };

  const handleReschedule = (appointment: Appointment) => {
    setModalAppointment(appointment);
    setDetailModalVisible(false); // Close detail modal
    setRescheduleModalVisible(true);
  };

  const handleChangeTechnician = (appointment: Appointment) => {
    setModalAppointment(appointment);
    setDetailModalVisible(false); // Close detail modal
    setTechChangeModalVisible(true);
  };

  const handleCancel = (appointment: Appointment) => {
    setModalAppointment(appointment);
    setDetailModalVisible(false); // Close detail modal
    setCancelModalVisible(true);
  };

  const handleModalSuccess = () => {
    // Close all modals and clear state after successful operation
    setDetailModalVisible(false);
    setRescheduleModalVisible(false);
    setTechChangeModalVisible(false);
    setCancelModalVisible(false);
    setModalAppointment(null);
    // Reload appointments to reflect changes
    loadAppointments();
  };

  const handleDetailModalClose = () => {
    setDetailModalVisible(false);
    setModalAppointment(null);
  };

  const handleRescheduleModalClose = () => {
    setRescheduleModalVisible(false);
    setModalAppointment(null);
  };

  const handleTechChangeModalClose = () => {
    setTechChangeModalVisible(false);
    setModalAppointment(null);
  };

  const handleCancelModalClose = () => {
    setCancelModalVisible(false);
    setModalAppointment(null);
  };

  // Filter appointments by selected employees and exclude cancelled appointments
  const filteredAppointments = appointments
    .filter(apt => apt.status !== 'cancelled') // Exclude cancelled appointments from working schedule
    .filter(apt => {
      if (selectedEmployees.length === 0) return true;
      // Check if appointment has any of the selected employees
      if (selectedEmployees.includes(apt.technician_id)) return true;
      if (apt.technicians?.some(t => selectedEmployees.includes(t.technician_id))) return true;
      return false;
    });

  // Filter employees if any are selected
  const displayedEmployees = selectedEmployees.length > 0
    ? employees.filter(emp => selectedEmployees.includes(emp.id))
    : employees;

  const renderView = () => {
    switch (viewMode) {
      case 'cards':
        return (
          <EmployeeCardGrid
            date={selectedDate}
            employees={displayedEmployees}
            appointments={filteredAppointments}
            schedules={schedules}
            exceptions={exceptions}
            onAppointmentClick={handleAppointmentClick}
            onScheduleApprovedEstimate={handleScheduleApprovedEstimate}
          />
        );
      case 'day':
        return (
          <TimelineView
            date={selectedDate}
            appointments={filteredAppointments}
            employees={displayedEmployees}
            schedules={schedules}
            exceptions={exceptions}
            onAppointmentClick={handleAppointmentClick}
            onRefresh={loadAppointments}
          />
        );
      case 'week':
        return (
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <Text type="secondary">Week view coming soon...</Text>
          </div>
        );
      case 'month':
        return (
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <Text type="secondary">Month view coming soon...</Text>
          </div>
        );
      default:
        return null;
    }
  };

  // If showing cancelled view, render that instead
  if (showCancelledView) {
    return <CancelledAppointmentsView onBack={() => setShowCancelledView(false)} />;
  }

  return (
    <div className="scheduling-hub">
      {/* Header */}
      <div className="scheduling-hub-header" ref={headerRef}>
        <Row gutter={[16, 16]} align="middle" justify="space-between" style={{ width: '100%' }}>
          <Col xs={24} sm={12} md={10} lg={8} xl={8}>
            <Title
              level={2}
              style={{
                margin: 0,
                fontSize: containerWidth < 576 ? '20px' : containerWidth < 768 ? '22px' : '30px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              Scheduling Hub
            </Title>
            <Text
              type="secondary"
              style={{
                fontSize: containerWidth < 768 ? '12px' : '14px',
                display: containerWidth < 576 ? 'none' : 'block'
              }}
            >
              Drag and drop appointments to schedule
            </Text>
          </Col>
          <Col xs={24} sm={12} md={14} lg={16} xl={16}>
            {containerWidth < 1100 ? (
              // Mobile/Tablet: 2x2 grid (below 1100px to prevent overlap)
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                    size="middle"
                    block
                  >
                    {containerWidth < 576 ? '' : 'Refresh'}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    icon={<StopOutlined />}
                    onClick={() => setShowCancelledView(true)}
                    size="middle"
                    block
                  >
                    {containerWidth < 576 ? 'Cancelled' : containerWidth < 900 ? 'Cancelled' : 'Cancelled Appts'}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    type="default"
                    icon={<FileTextOutlined />}
                    onClick={handleCreateEstimate}
                    size="middle"
                    block
                    style={{ backgroundColor: '#52c41a', color: 'white', borderColor: '#52c41a' }}
                  >
                    {containerWidth < 576 ? 'Estimate' : 'New Estimate'}
                  </Button>
                </Col>
                <Col span={12}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateAppointment}
                    size="middle"
                    block
                  >
                    {containerWidth < 576 ? 'Quick Book' : 'Quick Appt'}
                  </Button>
                </Col>
              </Row>
            ) : (
              // Desktop: Single row (1100px and above)
              <Space size="small" style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  size="large"
                >
                  Refresh
                </Button>
                <Button
                  icon={<StopOutlined />}
                  onClick={() => setShowCancelledView(true)}
                  size="large"
                >
                  Cancelled Appointments
                </Button>
                <Button
                  type="default"
                  icon={<FileTextOutlined />}
                  onClick={handleCreateEstimate}
                  size="large"
                  style={{ backgroundColor: '#52c41a', color: 'white', borderColor: '#52c41a' }}
                >
                  New Estimate
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateAppointment}
                  size="large"
                >
                  Quick Appointment
                </Button>
              </Space>
            )}
          </Col>
        </Row>
      </div>

      {/* Controls */}
      <Card className="scheduling-hub-controls" size="small">
        <Space size="large" style={{ width: '100%', justifyContent: 'space-between' }}>
          <ViewToggle value={viewMode} onChange={setViewMode} />
          <DateNavigator value={selectedDate} onChange={handleDateChange} viewMode={viewMode} />
          <EmployeeFilter
            employees={employees}
            selectedEmployees={selectedEmployees}
            onChange={handleEmployeeFilterChange}
          />
        </Space>
      </Card>

      {/* Main View */}
      <div className="scheduling-hub-content">
        <Spin spinning={loading} size="large">
          {renderView()}
        </Spin>
      </div>

      {/* Quick Booking Drawer */}
      <QuickBookingDrawer
        visible={drawerVisible}
        appointment={selectedAppointment}
        date={selectedDate}
        employees={employees}
        onClose={handleDrawerClose}
      />

      {/* Appointment Editing Modals */}
      <AppointmentDetailModal
        key={modalAppointment?.id || 'empty'}
        visible={detailModalVisible}
        appointment={modalAppointment}
        onClose={handleDetailModalClose}
        onReschedule={handleReschedule}
        onChangeTechnician={handleChangeTechnician}
        onCancel={handleCancel}
      />

      <RescheduleModal
        visible={rescheduleModalVisible}
        appointment={modalAppointment}
        onClose={handleRescheduleModalClose}
        onSuccess={handleModalSuccess}
      />

      <TechnicianChangeModal
        visible={techChangeModalVisible}
        appointment={modalAppointment}
        onClose={handleTechChangeModalClose}
        onSuccess={handleModalSuccess}
      />

      <CancelAppointmentModal
        visible={cancelModalVisible}
        appointment={modalAppointment}
        onClose={handleCancelModalClose}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};
